/**
 * ActionCardRenderer — switches on `card.kind` and renders the right
 * `@ordo/ui/action-card-*` primitive. Lifted verbatim from
 * `apps/agent/client/src/components/chat/action-card-renderer.tsx`.
 */

import type { ActionCard, ActionCardResolution } from '@ordo/agent-tools'
import { ChoiceCard } from '@ordo/ui/action-card-choice'
import { ConfirmCard } from '@ordo/ui/action-card-confirm'
import { DiffCard } from '@ordo/ui/action-card-diff'
import { FormCard } from '@ordo/ui/action-card-form'
import { PlanCard } from '@ordo/ui/action-card-plan'
import { PreviewCard } from '@ordo/ui/action-card-preview'
import { ResultCard } from '@ordo/ui/action-card-result'
import { StatusCard } from '@ordo/ui/action-card-status'

import type { PendingChatCard, ResolvedChatCard } from '../types'

export function ActionCardRenderer({
  pending,
  onResolve,
}: {
  pending: PendingChatCard
  onResolve: (cardId: string, resolution: ActionCardResolution) => void
}) {
  return <CardSwitch card={pending.card} cardId={pending.cardId} onResolve={onResolve} />
}

export function ResolvedActionCardRenderer({ card, resolved }: { card: ActionCard; resolved: ResolvedChatCard }) {
  return <CardSwitch card={card} cardId={resolved.cardId} resolution={resolved.resolution} />
}

function CardSwitch({
  card,
  cardId,
  resolution,
  onResolve,
}: {
  card: ActionCard
  cardId: string
  resolution?: ActionCardResolution
  onResolve?: (cardId: string, resolution: ActionCardResolution) => void
}) {
  switch (card.kind) {
    case 'confirm': {
      const decision = resolution?.kind === 'confirm' ? resolution.decision : null
      return (
        <ConfirmCard
          slug={card.attribution ?? 'tool'}
          summary={card.summary}
          rationale={card.rationale}
          details={card.details}
          destructive={card.destructive}
          approveLabel={card.approveLabel}
          skipLabel={card.skipLabel}
          resolution={decision === 'approve' ? 'approved' : decision === 'skip' ? 'skipped' : null}
          onApprove={() => onResolve?.(cardId, { kind: 'confirm', decision: 'approve' })}
          onSkip={() => onResolve?.(cardId, { kind: 'confirm', decision: 'skip' })}
        />
      )
    }
    case 'choice': {
      const resolvedSelectedIds = resolution?.kind === 'choice' ? resolution.selectedIds : undefined
      return (
        <ChoiceCard
          title={card.title}
          description={card.description}
          attribution={card.attribution}
          options={card.options}
          mode={card.mode}
          confirmLabel={card.confirmLabel}
          resolvedSelectedIds={resolvedSelectedIds}
          onConfirm={(ids) => onResolve?.(cardId, { kind: 'choice', selectedIds: ids })}
          onCancel={() => onResolve?.(cardId, { kind: 'cancelled' })}
        />
      )
    }
    case 'form': {
      const resolvedValues = resolution?.kind === 'form' ? resolution.values : undefined
      return (
        <FormCard
          title={card.title}
          description={card.description}
          attribution={card.attribution}
          fields={card.fields}
          submitLabel={card.submitLabel}
          resolvedValues={resolvedValues}
          onSubmit={(values) => onResolve?.(cardId, { kind: 'form', values })}
          onCancel={() => onResolve?.(cardId, { kind: 'cancelled' })}
        />
      )
    }
    case 'preview':
      return (
        <PreviewCard
          title={card.title}
          description={card.description}
          attribution={card.attribution}
          bodyMarkdown={card.bodyMarkdown}
          actions={card.actions}
        />
      )
    case 'diff':
      return (
        <DiffCard
          title={card.title}
          description={card.description}
          attribution={card.attribution}
          before={card.before}
          after={card.after}
          language={card.language}
        />
      )
    case 'status':
      return (
        <StatusCard
          title={card.title}
          description={card.description}
          attribution={card.attribution}
          state={card.state}
          progress={card.progress}
          stages={card.stages}
          cancellable={card.cancellable}
        />
      )
    case 'result':
      return (
        <ResultCard
          title={card.title}
          description={card.description}
          attribution={card.attribution}
          state={card.state}
          summary={card.summary}
          actions={card.actions}
        />
      )
    case 'plan': {
      const planResolved = resolution !== undefined && resolution.kind !== 'cancelled' && resolution.kind !== 'timeout'
      return (
        <PlanCard
          title={card.title}
          description={card.description}
          attribution={card.attribution}
          steps={card.steps.map((s) => ({
            id: s.id,
            toolSlug: s.toolSlug,
            summary: s.summary,
            args: s.args,
            dependsOn: s.dependsOn,
            state: s.state,
          }))}
          runAllLabel={card.runAllLabel}
          resolved={planResolved}
          onAction={(planAction) => {
            if (planAction.action === 'cancel') {
              onResolve?.(cardId, { kind: 'cancelled' })
              return
            }
            const payload = {
              kind: 'plan' as const,
              action: planAction.action,
              ...(planAction.action !== 'runAll' && 'stepIndex' in planAction
                ? { stepIndex: planAction.stepIndex }
                : {}),
              ...(planAction.action === 'editStep' && planAction.editedArgs !== undefined
                ? { editedArgs: planAction.editedArgs }
                : {}),
            }
            onResolve?.(cardId, payload as ActionCardResolution)
          }}
        />
      )
    }
    default:
      return null
  }
}
